import { ReactNode, useEffect, useState } from "react"
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Button, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { Loader2, Plus, type LucideIcon } from "lucide-react"

import { ConfirmDialog } from "@/components/dialogs"

import { BaseInlineInput } from "./base"

export interface MultiManagerItem {
  [key: string]: unknown
}

export interface InlineMultiManagerProps<T extends MultiManagerItem> {
  label?: ReactNode
  LabelIcon?: LucideIcon
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  placeholder?: string
  items: T[]
  primaryItem?: T | null
  onSave: (items: T[]) => void | Promise<void>
  isSaving?: boolean
  disabled?: boolean
  renderItem: (item: T, onDelete: () => void) => ReactNode
  renderAddForm: (onAdd: (item: Partial<T>) => void, onClose: () => void) => ReactNode
  renderPrimaryValue?: (item: T | null) => ReactNode
  itemIdKey?: keyof T
  displayKey?: keyof T
  addButtonLabel?: string
  emptyMessage?: string
  sortable?: boolean
  deletable?: boolean
  onItemClick?: (item: T) => void
  maxHeight?: string
  showAddFormLoader?: boolean
  fixedLabelWidth?: string
  alignLabel?: "center" | "start"
  confirmDelete?: {
    title: string
    getDescription?: (item: T) => string
  }
}

export function InlineMultiManager<T extends MultiManagerItem>({
  label,
  LabelIcon,
  hideLabel = false,
  size,
  placeholder = "No items",
  items = [],
  primaryItem,
  onSave,
  isSaving = false,
  disabled = false,
  renderItem,
  renderAddForm,
  renderPrimaryValue,
  itemIdKey = "id" as keyof T,
  displayKey,
  addButtonLabel = "Add item",
  emptyMessage,
  sortable = true,
  deletable = true,
  onItemClick,
  maxHeight = "max-h-75",
  showAddFormLoader = true,
  fixedLabelWidth,
  alignLabel,
  confirmDelete,
}: InlineMultiManagerProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [savingNewItem, setSavingNewItem] = useState(false)
  const [orderedItems, setOrderedItems] = useState<T[]>([])
  const [itemToDelete, setItemToDelete] = useState<T | null>(null)

  const activeItems = items.filter((item) => !item._destroy)
  const isEmpty = activeItems.length === 0

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    const sortedItems = [...items].filter((item) => !item._destroy)
    if (primaryItem) {
      sortedItems.sort((a, b) => {
        if (a[itemIdKey] === primaryItem[itemIdKey]) return -1
        if (b[itemIdKey] === primaryItem[itemIdKey]) return 1
        return 0
      })
    }
    setOrderedItems(sortedItems)
  }, [items, primaryItem, itemIdKey])

  function handleDragEnd(event: DragEndEvent) {
    if (!sortable) return

    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = orderedItems.findIndex((item) => item[itemIdKey] === active.id)
      const newIndex = orderedItems.findIndex((item) => item[itemIdKey] === over.id)

      const reorderedItems = arrayMove(orderedItems, oldIndex, newIndex)
      setOrderedItems(reorderedItems)

      const allItems = [...items]
      const updatedItems = allItems.map((item) => {
        if (item._destroy) return item

        const orderIndex = reorderedItems.findIndex((ordered) => ordered[itemIdKey] === item[itemIdKey])
        return {
          ...item,
          position: orderIndex,
          is_primary: orderIndex === 0,
        }
      })

      onSave(updatedItems)
    }
  }

  function handleDeleteItem(itemId: string | number) {
    if (!deletable) return

    const updatedItems = items.map((item) => (item[itemIdKey] === itemId ? { ...item, _destroy: true } : item))

    const remainingItems = updatedItems.filter((item) => !item._destroy)
    if (remainingItems.length > 0) {
      remainingItems[0] = { ...remainingItems[0], is_primary: true }
    }

    onSave(updatedItems)
  }

  function requestDeleteItem(item: T) {
    if (!deletable) return

    if (confirmDelete) {
      setItemToDelete(item)
    } else {
      handleDeleteItem(item[itemIdKey] as string | number)
    }
  }

  function confirmDeleteItem() {
    if (itemToDelete) {
      handleDeleteItem(itemToDelete[itemIdKey] as string | number)
      setItemToDelete(null)
    }
  }

  function cancelDeleteItem() {
    setItemToDelete(null)
  }

  async function handleAddItem(newItemData: Partial<T>) {
    setSavingNewItem(true)
    setShowAddForm(false)

    try {
      const newItem = {
        ...newItemData,
        is_primary: activeItems.length === 0,
        _destroy: false,
      } as unknown as T

      await onSave([...items, newItem])
    } finally {
      setSavingNewItem(false)
    }
  }

  function handleCloseAddForm() {
    setShowAddForm(false)
  }

  const displayValue = primaryItem
    ? renderPrimaryValue
      ? renderPrimaryValue(primaryItem)
      : displayKey
        ? String(primaryItem[displayKey])
        : `Item ${primaryItem[itemIdKey]}`
    : null

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
      alignLabel={alignLabel}
    >
      <div className="relative w-full">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <div
              tabIndex={0}
              role="button"
              aria-haspopup="dialog"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setIsOpen(true)
                }
              }}
              className={`hover:bg-muted focus-visible:border-input focus-visible:ring-ring ring-offset-background min-h-8 w-full cursor-pointer rounded-md bg-transparent px-2 py-1 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
                isEmpty ? "text-muted-foreground" : ""
              } ${disabled ? "cursor-default opacity-50" : ""} flex ${alignLabel === "start" ? "items-start" : "items-center"}`}
              onClick={primaryItem && onItemClick ? () => onItemClick(primaryItem) : undefined}
            >
              {displayValue || <span className="text-input-placeholder truncate">{emptyMessage || placeholder}</span>}
            </div>
          </PopoverTrigger>
          <PopoverContent className="space-y-2 px-2 py-2" align="start">
            {orderedItems.length > 0 && (
              <div className={maxHeight}>
                {sortable && orderedItems.length > 1 ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={orderedItems.map((item) => item[itemIdKey] as string | number)}
                      strategy={verticalListSortingStrategy}
                    >
                      {orderedItems.map((item) => (
                        <div key={String(item[itemIdKey])}>{renderItem(item, () => requestDeleteItem(item))}</div>
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  orderedItems.map((item) => (
                    <div key={String(item[itemIdKey])}>{renderItem(item, () => requestDeleteItem(item))}</div>
                  ))
                )}
              </div>
            )}

            {showAddFormLoader && savingNewItem && (
              <div className="flex items-center justify-center p-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}

            {showAddForm && renderAddForm(handleAddItem, handleCloseAddForm)}

            {!showAddForm && !savingNewItem && (
              <Button
                variant="ghost"
                className="flex w-full items-center justify-start gap-2 rounded-sm"
                size="xs"
                onClick={() => setShowAddForm(true)}
              >
                <Plus />
                {addButtonLabel}
              </Button>
            )}
          </PopoverContent>
        </Popover>
        {isSaving && <Loader2 className="text-icon absolute top-2 right-2 h-4 w-4 animate-spin" />}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          open={itemToDelete !== null}
          onOpenChange={(open) => !open && cancelDeleteItem()}
          title={confirmDelete.title}
          description={
            itemToDelete && confirmDelete.getDescription
              ? confirmDelete.getDescription(itemToDelete)
              : "This action cannot be undone."
          }
          dismiss={"explicit"}
          cancel={{ onClick: cancelDeleteItem }}
          action={{ label: "Delete", onClick: confirmDeleteItem }}
        />
      )}
    </BaseInlineInput>
  )
}
